import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import Slider from '@material-ui/core/Slider';
import isEmpty from 'lodash/isEmpty';

import { fansMarks } from 'utils/helpers';
import { closeModal } from 'features/utilsSlice';
import { followersMarks, formatLargeNumber } from 'utils/helpers';
import {
  setDiscoverPopularityConditions,
  setDiscoverRating,
  setDiscoverFilter,
  setDiscoverGender,
  setDiscoverTraitIds,
  setDiscoverCountry,
} from 'features/discoverFiltersSlice';
import useModalOpen from 'hooks/useModalOpen';
import TransitionDialog from './TransitionDialog';
import SwitchTabs from './SwitchTabs';
import ScaledSlider from './ScaledSlider';
import BottomButtons from './BottomButtons';
import FilterTraits from './FilterTraits';
import styles from 'styles/components/DashboardFilter.module.scss';

const useStyles = makeStyles(theme => ({
  paper: {
    height: '70%',
    marginTop: 'auto',

    [theme.breakpoints.up('md')]: {
      width: '60%',
      margin: '0 auto',
      borderRadius: 10,
    },

    [theme.breakpoints.up('lg')]: {
      width: '40%',
    },
  },

  paperFullScreenDialog: {
    [theme.breakpoints.up('md')]: {
      width: '100%',
    },
  },
}));

const filterName = {
  0: 'all',
  1: 'used',
  2: 'following',
};

const filterNumber = {
  all: 0,
  used: 1,
  following: 2,
};

const genderName = {
  0: 'all',
  1: 'female',
  2: 'male',
};

const genderNumber = {
  all: 0,
  female: 1,
  male: 2,
};

const noBorderBottom = { borderBottom: 'none' };

export const scale = (marks, value) =>
  marks.find(mark => mark.value === value)?.scaledValue || 0.01;

export const normalizeValue = (marks, value) =>
  marks.find(mark => mark.scaledValue === value)?.value || 0;

const FilterDialog = ({ selectedNetworks }) => {
  const [selectedTraits, setSelectedTraits] = useState({});
  const [selectedTraitIds, setSelectedTraitIds] = useState([]);
  const [gender, setGender] = useState(0);
  const [filter, setFilter] = useState(0);
  const [rating, setRating] = useState(0);
  const [popularityConditions, setPopularityConditions] = useState({});

  const selectedTraitsArray = Object.values(selectedTraits).flat();
  const traitIds = selectedTraitsArray.map(trait => trait.id);

  const dispatch = useDispatch();
  const classes = useStyles();

  const discoverFilter = useSelector(state => state.discoverFilters.filter);
  const discoverTraits = useSelector(state => state.discoverFilters.traitIds);
  const discoverRating = useSelector(state => state.discoverFilters.rating);
  const discoverGender = useSelector(state => state.discoverFilters.gender);
  const discoverPopularityConditions = useSelector(
    state => state.discoverFilters.popularityConditions
  );

  const open = useModalOpen('Filter Influencers');

  const handleClose = () => {
    if (discoverFilter !== 0) setFilter(0);
    if (!discoverRating) setRating(0);
    if (isEmpty(discoverPopularityConditions)) setPopularityConditions({});
    if (discoverTraits?.length > 0) setSelectedTraitIds([]);

    dispatch(closeModal('Filter Influencers'));
  };

  const handleReset = () => {
    dispatch(setDiscoverFilter('all'));
    dispatch(setDiscoverGender('all'));
    dispatch(setDiscoverPopularityConditions({}));
    dispatch(setDiscoverRating(0));
    dispatch(setDiscoverTraitIds([]));
    dispatch(setDiscoverCountry('all'));
    handleClose();
  };

  const handleApply = () => {
    dispatch(setDiscoverPopularityConditions(popularityConditions));
    dispatch(setDiscoverRating(rating));
    dispatch(setDiscoverFilter(filterName[filter]));
    dispatch(setDiscoverGender(genderName[gender]));
    dispatch(setDiscoverTraitIds(traitIds));
    handleClose();
  };

  useEffect(() => {
    if (discoverFilter) setFilter(filterNumber[discoverFilter]);
    if (discoverGender) setGender(genderNumber[discoverGender]);
    if (discoverRating) setRating(discoverRating);
    if (discoverTraits) setSelectedTraitIds(discoverTraits);
    if (discoverPopularityConditions) setPopularityConditions(discoverPopularityConditions);
  }, [
    discoverFilter,
    discoverRating,
    discoverPopularityConditions,
    discoverGender,
    discoverTraits,
  ]);

  return (
    <TransitionDialog
      open={open}
      handleClose={handleClose}
      paperProps={{ className: classes.paper }}
      dialogStyles={classes.paperFullScreenDialog}
      desktopDirection="up"
      direction="up"
      noCloseIcon
    >
      <div className={styles.header}>Filter</div>

      <div className={styles.box}>
        <h3>Filter influencers by their traits:</h3>

        <FilterTraits
          selectedTraitsArray={selectedTraitsArray}
          setSelectedTraits={setSelectedTraits}
          selectedTraitIds={selectedTraitIds}
        />
      </div>

      <div className={styles.box}>
        <h3>Filter influencers by</h3>

        <SwitchTabs
          labels={['All', 'Used before', 'Favorites']}
          value={filter}
          setValue={setFilter}
        />
      </div>

      <div className={styles.box}>
        <div className={styles.sliderHeader}>
          <h3>Minimum rating</h3>

          <h3 className="color-main">{rating}%</h3>
        </div>

        <Slider
          value={rating}
          onChange={(_, newValue) => setRating(newValue)}
          min={0}
          max={100}
          step={1}
        />
      </div>

      <div className={styles.box}>
        <div className={styles.sliderHeader}>
          <h3>Filter by gender</h3>
        </div>

        <SwitchTabs labels={['All', 'Female', 'Male']} value={gender} setValue={setGender} />
      </div>

      {selectedNetworks()
        .filter(network => !['OnlyFans', 'LoyalFans'].includes(network.name))
        .map((network, index) => (
          <div key={network.id}>
            <div
              className={styles.box}
              style={index + 1 === selectedNetworks().length ? noBorderBottom : {}}
            >
              <div className={styles.sliderHeader}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Image
                    src={network.active_icon_url}
                    width={30}
                    height={30}
                    alt={network.name}
                    unoptimized
                  />
                  <h3 style={{ marginLeft: 7 }}>Followers</h3>
                </div>

                <h3 className="color-main">
                  {popularityConditions[network.id]?.followers[0]
                    ? `${formatLargeNumber(
                        popularityConditions[network.id]?.followers[0]
                      )} - ${formatLargeNumber(popularityConditions[network.id]?.followers[1])}`
                    : `${formatLargeNumber(1000)}`}
                </h3>
              </div>

              <ScaledSlider
                count={popularityConditions[network.id]?.followers || [1000, 1000]}
                setCount={(_, scaledValue) => {
                  setPopularityConditions(prevState => ({
                    ...prevState,
                    [network.id]: {
                      followers: scaledValue,
                    },
                  }));
                }}
                marks={followersMarks}
              />
            </div>
          </div>
        ))}

      {selectedNetworks()
        .filter(network => ['OnlyFans', 'LoyalFans'].includes(network.name))
        .map(network => (
          <div className={styles.box} key={network.id}>
            <div className={styles.sliderHeader}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Image
                  src={network.active_icon_url}
                  width={30}
                  height={30}
                  alt={network.name}
                  unoptimized
                />
                <h3 style={{ marginLeft: 7 }}>Top %</h3>
              </div>

              <h3 className="color-main">
                {popularityConditions[network.id]?.top_creator_percentage?.[1] || 0.01}%{' '}
              </h3>
            </div>

            <Slider
              value={normalizeValue(
                fansMarks,
                popularityConditions[network.id]?.top_creator_percentage?.[1] || 0.01
              )}
              onChange={(_, newValue) => {
                setPopularityConditions(prevState => ({
                  ...prevState,
                  [network.id]: { top_creator_percentage: [0, scale(fansMarks, newValue)] },
                }));
              }}
              scale={value => scale(fansMarks, normalizeValue(fansMarks, value))}
              marks={fansMarks}
              max={400}
              min={0}
              step={25}
            />
          </div>
        ))}

      <BottomButtons
        text="Reset"
        textContained="Apply"
        handleClick={handleApply}
        handleClose={handleReset}
        sticky
      />
    </TransitionDialog>
  );
};

export default FilterDialog;
